// React
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// External Components
import ReactGA from "react-ga4";

// Internal Components
import Header from "../../components/layouts/Header/Header";
import TestFooter from "../../components/layouts/Footer/TestFooter";
import { ErrorMessage } from "../../components/ui/Error/ErrorMessage";

// User Actions
import { updateDemographics } from "../../actions/userActions";

// Page Data
import {
  AGE_BANDS,
  COUNTRIES,
  WORK_CONTEXTS,
  GENDERS,
  EDUCATION_LEVELS,
  DEMOGRAPHIC_PURPOSE,
} from "../../constants/demographicOptions";

// Styling
import "./aboutyou.css";

const QUESTIONS = [
  { field: "ageBand", label: "How old are you?", options: AGE_BANDS },
  { field: "country", label: "Where do you live?", options: COUNTRIES },
  {
    field: "workContext",
    label: "What does your working life look like?",
    options: WORK_CONTEXTS,
  },
  { field: "gender", label: "How do you describe your gender?", options: GENDERS },
  {
    field: "education",
    label: "How far did you go in formal education?",
    options: EDUCATION_LEVELS,
  },
];

/**
 * Wave 2 of the demographics ask — offered only after the practice has returned
 * something, per product principle 2 ("return something before asking for more").
 *
 * Every field is optional and every list carries "Prefer not to say". Skipping
 * is recorded as a decline rather than silently ignored: without a decline count
 * there is no response rate, and without a response rate M-03 cannot be read
 * for bias.
 */
export default function AboutYouScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const userUpdate = useSelector((state) => state.userUpdate);
  const { loading, error, success } = userUpdate;

  const existing = useMemo(
    () => (userInfo && userInfo.demographic) || {},
    [userInfo],
  );

  const [answers, setAnswers] = useState({
    ageBand: existing.ageBand || "",
    country: existing.country || "",
    workContext: existing.workContext || "",
    gender: existing.gender || "",
    education: existing.education || "",
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    ReactGA.event("demographics_prompted", { wave: 2 });
  }, []);

  // Navigate only once the save has actually landed. The old screen dispatched
  // and stayed put, which read to the user as a dead button.
  useEffect(() => {
    if (submitted && success) {
      navigate("/today");
    }
  }, [submitted, success, navigate]);

  const setField = (field) => (e) =>
    setAnswers((prev) => ({ ...prev, [field]: e.target.value }));

  const answeredCount = Object.values(answers).filter(Boolean).length;

  const submitHandler = (e) => {
    e.preventDefault();
    if (answeredCount === 0) return;

    const payload = {};
    Object.entries(answers).forEach(([field, value]) => {
      if (value) payload[field] = value;
    });

    // Behaviour, never content: how many were answered, not what was answered.
    ReactGA.event("demographics_completed", {
      wave: 2,
      fields_answered: answeredCount,
    });

    setSubmitted(true);
    dispatch(updateDemographics(payload));
  };

  const skipHandler = () => {
    ReactGA.event("demographics_skipped", { wave: 2 });
    dispatch(updateDemographics({ declined: true }));
    navigate("/today");
  };

  return (
    <>
      <Header variant="light" />
      <section id="aboutYouScreen">
        <section className="aboutYou__container">
          <p className="aboutYou__title">About you</p>
          <p className="aboutYou__intro">
            Five optional questions. Answer the ones you want to and leave the
            rest — there is no wrong amount.
          </p>
          <p className="aboutYou__purpose">{DEMOGRAPHIC_PURPOSE}</p>

          <form className="aboutYou__form" onSubmit={submitHandler}>
            {QUESTIONS.map(({ field, label, options }) => (
              <div className="aboutYou__row" key={field}>
                <label htmlFor={field}>{label}</label>
                <select
                  id={field}
                  value={answers[field]}
                  onChange={setField(field)}
                >
                  <option value="">Choose one, or leave blank</option>
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            ))}

            {error && <ErrorMessage variant="danger">{error}</ErrorMessage>}

            <div className="aboutYou__actions">
              {/* type="button" matters: an untyped button inside a form
                  submits it, which is what broke the previous screen. */}
              <button
                type="button"
                className="aboutYou__button aboutYou__button--quiet"
                onClick={skipHandler}
                disabled={loading}
              >
                Not now
              </button>
              <button
                type="submit"
                className="aboutYou__button"
                disabled={loading || answeredCount === 0}
              >
                {loading ? "Saving…" : "Save"}
              </button>
            </div>
          </form>

          <p className="aboutYou__footnote">
            You can change or erase these answers at any time from{" "}
            <a href="/my-account/my-data">Data &amp; Privacy Control</a>.
          </p>
        </section>
      </section>
      <TestFooter />
    </>
  );
}
